import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../core/api/client';

/**
 * Shown once, after a driver's first sign-in, before they can work.
 *
 * California requires staff to be told what personal information is collected
 * about them and why, and this app records precise location — sensitive
 * personal information under CPRA. Showing the notice is the obligation;
 * recording the acknowledgement is what makes it demonstrable later.
 *
 * Deliberately NOT a modal over the route: a driver who has to dismiss
 * something to start work will dismiss it without reading. This is a full
 * screen with the notice one tap away and a checkbox that must be ticked
 * deliberately — the button stays disabled until it is.
 *
 * It fails OPEN. If the check cannot reach the server, the driver works: a
 * consent screen must never be the reason a medical delivery does not happen,
 * and the prompt simply returns next time.
 */

/** Bump when the notice changes materially; everyone is then asked again. */
export const PRIVACY_NOTICE_VERSION = '2026-09-11';

/*
 * Remembered per phone. Asked once, answered once.
 *
 * The check is a network round trip, and it sat in front of the route screen
 * every single time the app opened — up to twelve seconds before a driver saw
 * any work, for a question already answered weeks ago. Once this phone has
 * accepted the current notice, the gate opens immediately and the server is
 * still asked in the background.
 *
 * The version is part of the key, so changing the notice asks everybody again.
 */
const ACCEPTED_KEY = 'jw.privacyAccepted';

function acceptedHere(version: string): boolean {
  try { return localStorage.getItem(ACCEPTED_KEY) === version; } catch { return false; }
}

function rememberAccepted(version: string): void {
  try { localStorage.setItem(ACCEPTED_KEY, version); } catch { /* fine */ }
}

export function PrivacyGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<'checking' | 'needed' | 'done'>(
    () => (acceptedHere(PRIVACY_NOTICE_VERSION) ? 'done' : 'checking'));
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      const { accepted } = await api.privacyAccepted(PRIVACY_NOTICE_VERSION);
      if (accepted) rememberAccepted(PRIVACY_NOTICE_VERSION);
      setState(accepted ? 'done' : 'needed');
    } catch {
      setState('done');   // fail open, on purpose — see the note above
    }
  }, []);

  useEffect(() => { void check(); }, [check]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      await api.acknowledgePrivacy(PRIVACY_NOTICE_VERSION);
      rememberAccepted(PRIVACY_NOTICE_VERSION);
      setState('done');
    } catch {
      setError('Could not save that — check your signal and try again.');
      setBusy(false);
    }
  };

  if (state === 'checking') {
    return <main className="screen"><p>Loading…</p></main>;
  }
  if (state === 'done') return <>{children}</>;

  return (
    <main className="screen">
      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt is set below */}
      <img className="empty-logo" src="/icons/logo.png"
           alt="JW Express Delivery" width={320} height={256} />
      <h1>Before you start</h1>
      <p className="hint">
        This app records where you are <strong>while you are on a route</strong>,
        and keeps a photo and signature for each delivery. It records nothing
        before you accept a route, after you finish, or once you sign out.
      </p>

      <p>
        <Link to="/privacy">Read the full privacy notice</Link>
      </p>

      <label className="consent">
        <input type="checkbox" checked={ticked}
               onChange={(e) => setTicked(e.target.checked)} />
        <span>I have read and understood the privacy notice.</span>
      </label>

      {error && <div className="banner error-banner">{error}</div>}

      <button className="btn primary big" disabled={!ticked || busy} onClick={accept}>
        {busy ? 'Saving…' : 'Continue'}
      </button>

      <p className="hint">
        Questions? Call the office — the numbers are under{' '}
        <Link to="/help">Need help</Link>.
      </p>
    </main>
  );
}
